/**
 * One preferred J-code / drug-name row in the Status Update modal.
 * @module SingleJcodeEntryRow
 */

import { InputField } from "@risalabs_frontend_org/oasis-ui-kit";
import { ChangeEvent } from "react";
import { Delete } from "../../../svg/delete";
import { PreferredJcodeEntry } from "./statusUpdateModal.types";

export type { PreferredJcodeEntry };

/** Props for {@link SingleJcodeEntryRow}. */
interface SingleJcodeEntryRowProps {
  /** Unique row id used to build input element ids. */
  id: string;
  /** Current preferred J-code value. */
  jCode: string;
  /** Current preferred drug name value. */
  drugName: string;
  /**
   * When true, omits the `label` prop on both InputFields (subsequent rows
   * under a shared header). Does not use InputField's `hideLabel`.
   */
  hideLabels?: boolean;
  /** When false, hides the delete control (e.g. for the sole remaining row). */
  showDelete?: boolean;
  /** When true, the input fields are required. */
  required?: boolean;
  /** Invoked when the preferred J-code input changes. */
  onJCodeChange: (id: string, value: string) => void;
  /** Invoked when the preferred drug name input changes. */
  onDrugNameChange: (id: string, value: string) => void;
  /** Invoked when the delete control is clicked. */
  onDelete: (id: string) => void;
}

/**
 * Renders preferred J-code and drug-name inputs with a delete control.
 *
 * When `hideLabels` is true, InputFields are rendered without a `label`
 * prop so rows align under the first labeled row. Field wrappers use
 * `min-w-0 flex-1` and `fullWidth` so two inputs share the row without
 * overflowing the modal. When `required` is true, labeled fields show as required.
 *
 * @input props - Controlled row values, required flag, and change handlers
 * @return JSX for a single preferred-drug entry row
 */
const SingleJcodeEntryRow = ({
  id,
  jCode,
  drugName,
  hideLabels = false,
  showDelete = true,
  required = false,
  onJCodeChange,
  onDrugNameChange,
  onDelete,
}: SingleJcodeEntryRowProps) => {
  /**
   * Forwards J-code input changes to the parent using the row id.
   *
   * @input event - Native change event from the J-code InputField
   */
  const handleJCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    onJCodeChange(id, event.target.value);
  };

  /**
   * Forwards drug-name input changes to the parent using the row id.
   *
   * @input event - Native change event from the drug-name InputField
   */
  const handleDrugNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onDrugNameChange(id, event.target.value);
  };

  return (
    <div className="single-jcode-entry-row--container flex items-baseline gap-3">
      <div className="min-w-0 flex-1">
        <InputField
          id={`preferred-jcode-${id}`}
          label={hideLabels ? undefined : "Preferred J-Code(s)"}
          fullWidth
          value={jCode}
          onChange={handleJCodeChange}
          required={!hideLabels && required}
        />
      </div>
      <div className="min-w-0 flex-1">
        <InputField
          id={`preferred-drug-name-${id}`}
          label={hideLabels ? undefined : "Preferred Drug Name"}
          fullWidth
          value={drugName}
          onChange={handleDrugNameChange}
          required={!hideLabels && required}
        />
      </div>
      {showDelete && (
        <div className="delete-icon mb-2 h-full shrink-0 self-end">
          <Delete height={16} width={16} onClick={() => onDelete(id)} />
        </div>
      )}
    </div>
  );
};

export default SingleJcodeEntryRow;
